/**
 * Phase 2C-iii · DXF Import Modal (Pro-tier).
 *
 * Four step flow:
 *   1. Upload file (+ optional unit override before commit)
 *   2. Pick outer polygon · toggle voids
 *   3. Assign BC per edge (uses parametric BC vocabulary)
 *   4. Commit → POST /api/geometries/{id}/dxf_commit
 *
 * The modal is only mounted when the user clicks "Import DXF" on the
 * geometry section toolbar AND their tier is Pro. The button itself is
 * gated upstream (see SetupTab geometry toolbar).
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

import { authService } from "@/services/authService";

const BC_OPTIONS = [
    { value: "ambient",   label: "Ambient air",       color: "#01696f" },
    { value: "insulated", label: "Insulated formwork", color: "#7b8b91" },
    { value: "adiabatic", label: "Adiabatic / symmetry", color: "#2f3a3d" },
    { value: "soil",      label: "Soil contact",       color: "#6a5d4d" },
];
const BC_DEFAULT = "ambient"; // fallback for un-assigned edges
const UNIT_OPTIONS = [
    { value: "",     label: "use DXF $INSUNITS" },
    { value: "mm",   label: "mm" },
    { value: "inch", label: "inch (× 25.4)" },
    { value: "cm",   label: "cm (× 10)" },
    { value: "m",    label: "m (× 1000)" },
];

const polyToPath = (pts, mapX, mapY) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${mapX(p[0]).toFixed(2)} ${mapY(p[1]).toFixed(2)}`).join(" ") + " Z";


export const DxfImportModal = ({ geometry, projectId, onClose, onCommitted }) => {
    /* eslint-disable-next-line no-unused-vars */
    const _ = projectId;
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [unitOverride, setUnitOverride] = useState("");
    const [preview, setPreview] = useState(null);   // parsed payload
    const [outerIdx, setOuterIdx] = useState(0);    // chosen outer
    const [voidIdxs, setVoidIdxs] = useState([]);   // chosen voids
    const [edgeBcs, setEdgeBcs] = useState([]);     // per-edge bc_type
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const polygons = preview?.polygons || [];
    const outer = polygons[outerIdx];
    const innerCandidates = polygons.filter((_, i) => i !== outerIdx);

    // When outer changes, reset edge-BCs to the default value.
    React.useEffect(() => {
        if (!outer) { setEdgeBcs([]); return; }
        const n = outer.points_mm.length;
        setEdgeBcs(Array.from({ length: n }, () => BC_DEFAULT));
    }, [outerIdx, outer]);

    const onUpload = async () => {
        setErr(null);
        if (!file) return setErr("Pick a DXF file first.");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            if (unitOverride) fd.append("unit_override", unitOverride);
            const res = await authService.http.post("/geometries/dxf_import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(res.data);
            setOuterIdx(0);
            setVoidIdxs([]);
            setStep(2);
        } catch (e) {
            const msg = e?.response?.data?.detail?.message || e?.message || "Upload failed.";
            setErr(msg);
        } finally { setBusy(false); }
    };

    const commit = async () => {
        setErr(null);
        if (!outer) return setErr("Pick an outer polygon first.");
        setBusy(true);
        try {
            const body = {
                outer_points_mm: outer.points_mm,
                void_polygons_mm: voidIdxs.map((i) => polygons[i].points_mm),
                boundary_segments: edgeBcs.map((bc, idx) => ({
                    edge_index: idx,
                    bc_type: bc,
                    v_from: idx,
                    v_to: (idx + 1) % outer.points_mm.length,
                })),
                unit_used: preview?.unit_used,
                insunits_code: preview?.insunits_code,
            };
            await authService.http.post(`/geometries/${geometry.id}/dxf_commit`, body);
            toast.success("DXF geometry committed.");
            onCommitted?.();
            onClose();
        } catch (e) {
            const msg = e?.response?.data?.detail?.message || e?.message || "Commit failed.";
            setErr(msg);
        } finally { setBusy(false); }
    };

    /* ---------- SVG preview transform (all-step preview) ---------- */
    const svgGeom = useMemo(() => {
        if (!outer) return null;
        const all = [outer, ...voidIdxs.map((i) => polygons[i])];
        const xs = all.flatMap((p) => p.points_mm.map((q) => q[0]));
        const ys = all.flatMap((p) => p.points_mm.map((q) => q[1]));
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const w = 520, h = 320;
        const pad = 24;
        const sx = (w - 2 * pad) / Math.max(1, maxX - minX);
        const sy = (h - 2 * pad) / Math.max(1, maxY - minY);
        const s = Math.min(sx, sy);
        const mapX = (x) => pad + (x - minX) * s;
        const mapY = (y) => h - pad - (y - minY) * s;  // flip Y for screen
        return { w, h, mapX, mapY };
    }, [outer, voidIdxs, polygons]);

    const stop = (e) => e.stopPropagation();

    return (
        <div onClick={onClose} data-testid="dxf-import-modal"
             className="fixed inset-0 z-50 bg-[#0e1416]/55 flex items-center justify-center p-4">
            <div onClick={stop} className="bg-[#ffffff] border border-[#d8dcdd] w-full max-w-[920px] max-h-[90vh] overflow-auto">
                {/* Header */}
                <div className="flex items-start justify-between px-6 py-5 border-b border-[#d8dcdd]">
                    <div>
                        <div className="tc-mono text-[10.5px] tracking-[0.18em] uppercase text-[#5a6669]">DXF import · Pro</div>
                        <h2 className="text-[20px] font-medium text-[#0e1416] mt-1">Import cross-section from DXF</h2>
                        <div className="tc-mono text-[10.5px] text-[#8a9498] mt-1">Step {step} of 3</div>
                    </div>
                    <button onClick={onClose} data-testid="dxf-import-close"
                            className="text-[#5a6669] hover:text-[#0e1416] p-1"><X size={18} strokeWidth={1.5} /></button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 flex flex-col gap-5">
                    {step === 1 && (
                        <section className="flex flex-col gap-4">
                            <p className="text-[13px] text-[#0e1416] leading-[1.6] max-w-[68ch]">
                                Upload a DXF containing one or more closed LWPOLYLINE / POLYLINE outlines.
                                Open polylines, ARC, CIRCLE and SPLINE are not supported in this release —
                                flatten and close the geometry in CAD before re-export.
                            </p>
                            <div className="flex flex-col gap-2">
                                <label className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">DXF file</label>
                                <input
                                    type="file" accept=".dxf,application/dxf"
                                    data-testid="dxf-import-file"
                                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="tc-mono text-[12.5px] file:border file:border-[#d8dcdd] file:bg-[#f6f7f6] file:px-3 file:py-1.5 file:mr-3 file:cursor-pointer file:text-[#0e1416]"
                                />
                            </div>
                            <div className="flex flex-col gap-2 max-w-[280px]">
                                <label className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">Unit override</label>
                                <select
                                    value={unitOverride}
                                    onChange={(e) => setUnitOverride(e.target.value)}
                                    data-testid="dxf-import-unit"
                                    className="tc-mono text-[12.5px] px-3 py-1.5 border border-[#d8dcdd] bg-[#ffffff]"
                                >
                                    {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                                </select>
                                <span className="tc-mono text-[10.5px] text-[#8a9498]">
                                    Leave blank to honour the DXF&apos;s <span className="text-[#0e1416]">$INSUNITS</span> header.
                                    Override only if the CAD source is unitless or wrong.
                                </span>
                            </div>
                        </section>
                    )}

                    {step >= 2 && preview && outer && svgGeom && (
                        <section className="flex flex-col gap-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 tc-mono text-[11px] text-[#5a6669]">
                                <div>$INSUNITS: <span className="text-[#0e1416]">{preview.insunits_name}</span> ({preview.insunits_code})</div>
                                <div>unit used: <span className="text-[#0e1416]">{preview.unit_used}</span> (× {preview.mm_per_unit} mm)</div>
                                <div>polygons found: <span className="text-[#0e1416]">{polygons.length}</span></div>
                                <div>vertices on outer: <span className="text-[#0e1416]">{outer.vertex_count}</span></div>
                            </div>
                            {(preview.warnings || []).length > 0 && (
                                <ul data-testid="dxf-import-warnings"
                                    className="border border-[#b3741f] bg-[#fbf4eb] px-4 py-3 text-[12px] text-[#0e1416] leading-[1.55]">
                                    {preview.warnings.map((w, i) => <li key={i} className="tc-mono">{w}</li>)}
                                </ul>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-5 items-start">
                                {/* SVG preview */}
                                <div className="border border-[#d8dcdd] bg-[#f6f7f6] p-2">
                                    <svg width={svgGeom.w} height={svgGeom.h} viewBox={`0 0 ${svgGeom.w} ${svgGeom.h}`}>
                                        <path d={polyToPath(outer.points_mm, svgGeom.mapX, svgGeom.mapY)}
                                              fill="#01696f" fillOpacity="0.06"
                                              stroke="#5a6669" strokeWidth="0.6" />
                                        {/* per-edge coloured stroke */}
                                        {outer.points_mm.map((p, idx) => {
                                            const a = outer.points_mm[idx];
                                            const b = outer.points_mm[(idx + 1) % outer.points_mm.length];
                                            const bc = edgeBcs[idx] || BC_DEFAULT;
                                            const c = BC_OPTIONS.find((o) => o.value === bc)?.color || "#0e1416";
                                            return (
                                                <line key={idx} x1={svgGeom.mapX(a[0])} y1={svgGeom.mapY(a[1])}
                                                      x2={svgGeom.mapX(b[0])} y2={svgGeom.mapY(b[1])}
                                                      stroke={c} strokeWidth="2.2" />
                                            );
                                        })}
                                        {voidIdxs.map((i) => (
                                            <path key={i} d={polyToPath(polygons[i].points_mm, svgGeom.mapX, svgGeom.mapY)}
                                                  fill="#ffffff" stroke="#b3741f" strokeWidth="0.8" strokeDasharray="3 2" />
                                        ))}
                                        {/* vertex labels v0..vN on outer */}
                                        {outer.points_mm.map((p, idx) => (
                                            <g key={`v-${idx}`}>
                                                <circle cx={svgGeom.mapX(p[0])} cy={svgGeom.mapY(p[1])} r="2.5" fill="#0e1416" />
                                                <text x={svgGeom.mapX(p[0]) + 6} y={svgGeom.mapY(p[1]) - 4}
                                                      className="tc-mono" fontSize="9" fill="#5a6669">v{idx}</text>
                                            </g>
                                        ))}
                                    </svg>
                                </div>

                                {/* Right pane: outer + voids + BCs */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-2">
                                        <label className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">Outer boundary</label>
                                        <select
                                            value={outerIdx}
                                            onChange={(e) => { const v = parseInt(e.target.value, 10); setOuterIdx(v); setVoidIdxs([]); }}
                                            data-testid="dxf-import-outer-select"
                                            className="tc-mono text-[12.5px] px-3 py-1.5 border border-[#d8dcdd] bg-[#ffffff]"
                                        >
                                            {polygons.map((p) => (
                                                <option key={p.index} value={p.index}>
                                                    polygon {p.index} · {p.vertex_count} vertices · area {Math.round(p.area_mm2)} mm²
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {innerCandidates.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            <label className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">Voids (optional)</label>
                                            <ul className="flex flex-col gap-1">
                                                {innerCandidates.map((p) => (
                                                    <li key={p.index} className="flex items-center gap-2 tc-mono text-[11.5px] text-[#0e1416]">
                                                        <input
                                                            type="checkbox"
                                                            data-testid={`dxf-import-void-${p.index}`}
                                                            checked={voidIdxs.includes(p.index)}
                                                            onChange={(e) => {
                                                                setVoidIdxs((curr) => e.target.checked
                                                                    ? [...curr, p.index]
                                                                    : curr.filter((x) => x !== p.index));
                                                            }}
                                                        />
                                                        polygon {p.index} · {p.vertex_count} vertices · area {Math.round(p.area_mm2)} mm²
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {step >= 3 && (
                                        <div className="flex flex-col gap-2">
                                            <label className="tc-mono text-[10.5px] tracking-widest uppercase text-[#5a6669]">Boundary condition per edge</label>
                                            <ul className="flex flex-col gap-1 max-h-[180px] overflow-auto pr-1">
                                                {outer.points_mm.map((_, idx) => (
                                                    <li key={idx} className="grid grid-cols-[60px_1fr] items-center gap-2 tc-mono text-[11px] text-[#0e1416]">
                                                        <span className="text-[#5a6669]">edge v{idx}→v{(idx + 1) % outer.points_mm.length}</span>
                                                        <select
                                                            value={edgeBcs[idx] || BC_DEFAULT}
                                                            onChange={(e) => {
                                                                const next = [...edgeBcs];
                                                                next[idx] = e.target.value;
                                                                setEdgeBcs(next);
                                                            }}
                                                            data-testid={`dxf-import-edge-${idx}`}
                                                            className="tc-mono text-[11.5px] px-2 py-1 border border-[#d8dcdd] bg-[#ffffff]"
                                                        >
                                                            {BC_OPTIONS.map((b) => (
                                                                <option key={b.value} value={b.value}>{b.label}</option>
                                                            ))}
                                                        </select>
                                                    </li>
                                                ))}
                                            </ul>
                                            <div className="flex flex-wrap gap-3 mt-1 tc-mono text-[10px] text-[#5a6669]">
                                                {BC_OPTIONS.map((b) => (
                                                    <span key={b.value} className="flex items-center gap-1.5">
                                                        <span style={{ background: b.color }} className="inline-block w-3 h-[3px]" />
                                                        {b.label}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    {err && (
                        <p data-testid="dxf-import-error"
                           className="tc-mono text-[11.5px] text-[#a13434] border border-[#a13434] bg-[#fbeaea] px-3 py-2">
                            {err}
                        </p>
                    )}
                </div>

                {/* Footer / actions */}
                <div className="px-6 py-4 border-t border-[#d8dcdd] flex justify-between items-center gap-3 bg-[#f6f7f6]">
                    <button onClick={onClose} className="tc-mono text-[11.5px] px-3 py-2 text-[#5a6669] hover:text-[#0e1416]">
                        Cancel
                    </button>
                    <div className="flex gap-2">
                        {step === 1 && (
                            <button
                                onClick={onUpload}
                                disabled={busy || !file}
                                data-testid="dxf-import-upload"
                                className="tc-mono text-[12px] px-5 py-2.5 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-40 text-[#ffffff] inline-flex items-center gap-2"
                            >
                                <Upload size={13} strokeWidth={1.5} />Parse DXF
                            </button>
                        )}
                        {step === 2 && (
                            <button
                                onClick={() => setStep(3)}
                                disabled={busy || !outer}
                                data-testid="dxf-import-step3"
                                className="tc-mono text-[12px] px-5 py-2.5 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-40 text-[#ffffff]"
                            >
                                Assign boundary conditions →
                            </button>
                        )}
                        {step === 3 && (
                            <button
                                onClick={commit}
                                disabled={busy}
                                data-testid="dxf-import-commit"
                                className="tc-mono text-[12px] px-5 py-2.5 bg-[#01696f] hover:bg-[#015a60] disabled:opacity-40 text-[#ffffff]"
                            >
                                {busy ? "Committing…" : "Commit geometry"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DxfImportModal;
